import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { PersonAttributes, PersonAttributesSlim } from '~app/models/person';
import { GuestBadgeComponent } from '~app/components/shared/guest-badge/guest-badge.component';

/**
 * v2.4 member-row presentation: a 4-column grid showing name, email, phone,
 * and a Remove action. Used by the team-edit modal for all four member
 * sections (Supervisors, Team Members, Dispatchers, Guests).
 *
 * The earlier draft included an avatar + supervisor-attribution sub-line.
 * Both were dropped to match the v2.4 Figma layout; if either comes back
 * in a later spec, restore from history.
 */
@Component({
  selector: 'ciao-team-member-row',
  standalone: true,
  imports: [CommonModule, GuestBadgeComponent],
  templateUrl: './team-member-row.component.html',
  styleUrl: './team-member-row.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamMemberRowComponent {
  // Accepts the full PersonAttributes (loaded by getMembersByRole) or the
  // slim shape (held by the POC control). Both expose id, displayName, and
  // displayNameDropdown; the row's getters use the richer fields when
  // available and fall back to dropdown-parsing otherwise.
  @Input() person!: PersonAttributes | PersonAttributesSlim;
  @Input() canRemove = false;

  @Output() remove = new EventEmitter<void>();

  /**
   * Mobile-only accordion: when collapsed, only the name + chevron + Remove
   * are visible; tapping the chevron expands the email and phone detail
   * lines. On desktop the chevron is hidden via media query and email/phone
   * always render in their own grid columns, so this flag has no effect
   * there.
   */
  expanded = false;

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  /**
   * Preferred email for display. Picks the @usda.gov email if the person has
   * one; otherwise falls back to whichever email Sequelize returned first.
   * Mirrors the server-side displayNameDropdown getter so the row's email
   * matches what other surfaces (POC dropdown, etc.) show for the same user.
   * Falls back to parsing the email out of displayNameDropdown when the
   * person was passed in slim form (no users array).
   */
  get email(): string | null {
    const full = this.person as PersonAttributes;
    const users = full?.users ?? [];
    if (users.length > 0) {
      const usda = users.find((u) =>
        u?.email?.toLowerCase().endsWith('@usda.gov')
      );
      return usda?.email ?? users[0]?.email ?? null;
    }
    return this.parseEmailFromDropdown(this.person?.displayNameDropdown);
  }

  get phone(): string | null {
    const full = this.person as PersonAttributes;
    // Prefer the person's default-flagged phone, matching the POC card's
    // hydratePocPhone — otherwise the two surfaces could show different
    // numbers for the same person. Falls back to the deprecated cellPhone.
    return full?.defaultPhone?.phoneNumber ?? full?.cellPhone ?? null;
  }

  /**
   * displayNameDropdown is formatted "First Last (email@domain)". Returns
   * the email portion lowercased, or null if no parenthesized email exists.
   */
  private parseEmailFromDropdown(
    dropdown: string | null | undefined
  ): string | null {
    if (!dropdown) return null;
    const m = dropdown.match(/\(([^()]+)\)\s*$/);
    return m ? m[1].trim().toLowerCase() : null;
  }

  onRemoveClick(): void {
    this.remove.emit();
  }
}
